import { INote, IDocument } from 'types/notes'
import callApi, { ENDPOINTS } from 'utils/api/callApi'
import { filePromises } from 'utils/fileUploadHelper'

export type NoteTypes = 'human' | 'ima_bot' | ''

export interface INotesRequest {
  id: string
  noteType?: NoteTypes
  noteableType?: string
}

export interface INotesSubmit {
  noteType?: NoteTypes
  noteableType?: string
  noteableId: string
  id?: string
  createdById?: number | string
  body: string
  callbackDate?: string
  callbackTime?: string
  needsCallback?: string
  calledBackAt?: boolean
}

/**
 * Retrieve all notes by id
 * @method GET
 */
export const getNotes = ({
  id,
  noteType = '',
  noteableType = 'Shipment',
}: INotesRequest): Promise<INote[]> =>
  callApi({
    endpoint: ENDPOINTS.notes,
    method: 'GET',
    dataKey: 'notes',
    params: {
      perPage: 100,
      filters: {
        noteableType,
        noteableId: id,
        noteType,
      },
    },
  })

/**
 * Submit a new note
 * @method POST
 */
export const submitNotes = ({
  noteableId,
  noteableType = 'Shipment',
  ...noteProps
}: INotesSubmit): Promise<INote> =>
  callApi({
    endpoint: ENDPOINTS.notes,
    method: 'POST',
    dataKey: 'note',
    body: {
      note: {
        ...noteProps,
        noteableType,
        noteableId,
      },
    },
  })

/**
 * Update existing note
 * @method PUT
 */
export const editNote = (noteProps: INotesSubmit): Promise<INote> =>
  callApi({
    endpoint: ENDPOINTS.notes,
    method: 'PUT',
    dataKey: 'note',
    id: noteProps.id,
    body: {
      note: noteProps,
    },
  })

interface IDocumentUpload {
  document: FileReader['result']
  documentFileName: File['name']
}

/**
 * Api call to upload a file to a note
 */
export const attachFileToNote = (noteId: string, document: IDocumentUpload): Promise<IDocument> =>
  callApi({
    endpoint: ENDPOINTS.notes,
    method: 'POST',
    id: noteId,
    group: 'note_documents',
    body: {
      note_document: {
        ...document,
      },
    },
  })

/**
 * Upload multiple files to a note. Returns array of document files
 */
export const attachMultipleFilesToNote = async (noteId: string, fileList: File[]) => {
  const fileUploads = await Promise.all(filePromises(fileList))
  const noteResponses = await Promise.all(
    fileUploads.map(file => {
      return attachFileToNote(noteId, {
        documentFileName: file.fileFileName,
        document: file.file,
      })
    })
  )

  return noteResponses
}
